import React, { useState, useRef, useEffect } from "react";
import sign_up from "../assets/images/sign_up.png";
import logo from "../assets/images/logo.png";
import apiClient from "../api/Apiservice";
import { BASE_URL } from "../api/devprofile";
import useLoginStatusStore from "../zustand/loginStatusStore";
import useUserDetailsStore from "../zustand/userDetailsStore";
import SignupAlert from "./SignupAlert";
import TermsAndConditionsPage from "./TermsAndConditionsPage";
import PrivacyPolicy from "./PrivacyPolicy";
import PrivacyPolicyUser from "./PrivacyPolicyUser";
import { toast } from "react-toastify";

interface SignupProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserInterface {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  notificationMode: string[];
  userId: string;
}

export default function SignupModal({ isOpen, onClose }: SignupProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    email: "",
    mobile: "",
  });
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setUserDetails } = useUserDetailsStore();
  const { setUserMobile, setIsLoginStatus, setUseruuId, useruuId } =
    useLoginStatusStore();
  const [userData, setUserData] = useState<UserInterface | {}>({});
  const [isOpenAlert, setIsOpenAlert] = useState(false);

  const [modalType, setModalType] = useState<
    "signup" | "terms" | "privacy" | null
  >("signup");
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setModalType("signup");
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value = e.target.value;

    if (["firstName", "lastName"].includes(e.target.name)) {
      value = value.replace(/[^a-zA-Z\s]/g, "");
      value = value.replace(/^\s+/, "");
      value = value.replace(/\s+/g, " ");
      value = value.replace(/(\s)(?=\s)/g, "");
    } else if (e.target.name === "mobile") {
      value = value.replace(/[^0-9]/g, "");
    }

    setFormData({ ...formData, [e.target.name]: value });
  };

  const getUserDetails = async (mobile: number, email: string) => {
    try {
      if (email) {
        const { data, status } = await apiClient.get(
          `${BASE_URL}/zoy_customer/getUser/${mobile}?email=${email}`
        );
        return data;
      }
    } catch (error) {
      // console.error("Error fetching user details in use auth:", error);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetails(
          Number(formData.mobile),
          formData.email
        );
        setUserData(response || {});
        if (response) {
          setUserDetails(response);
        } else {
          // console.error("No user details found.");
        }
      } catch (error) {
        // console.error("Error fetching user details:", error);
      }
    };
    fetchUserDetails();
  }, [formData.mobile]);

  const registerData = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    gender: formData.gender,
    email: formData.email,
    mobile: formData.mobile,
    notificationMode: ["whatsapp", "email", "sms"],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (/^[0-5]/.test(formData.mobile)) {
      setMobileError("Invalid Mobile Number");
      return;
    }

    setMobileError(null);

    try {
      const response = await apiClient.get(
        `${BASE_URL}/zoy_customer/checkPhoneNumber/${formData.mobile}`
      );
      const message = response.data.message;

      if (message === "Phone number exists") {
        toast("Mobile Number already exists. Please try with different one.");
        return;
      }

      const { data, status } = await apiClient.post(
        `${BASE_URL}/zoy_customer/registerUser`,
        registerData
      );

      const id = data.data;
      setUseruuId(id);
      setUserMobile(formData.mobile);
      setIsLoginStatus(true);

      if (status === 200 || status === 201) {
        setIsOpenAlert(true);
        setModalType(null);
        setFormData({
          firstName: "",
          lastName: "",
          gender: "",
          email: "",
          mobile: "",
        });
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast("Email already exists.Please try with different one.");
      } else {
        toast("An error occurred. Please try again later.");
        // console.error("Error:", error);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsOpenAlert(false);
      setModalType(null);
      onClose();
    }
  };

  // useEffect(() => {
  //   if (isOpen) {
  //     document.addEventListener("mousedown", handleClickOutside);
  //   } else {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   }

  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isOpen]);

  if (!isOpen && modalType === null) return null;

  const handleClose = () => {
    setIsOpenAlert(false);
    setModalType(null);
    onClose();
  };

  const handleTermsClick = () => {
    setModalType("terms");
  };

  const handlePrivacyClick = () => {
    setModalType("privacy");
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="flex flex-row h-[550px]" ref={modalRef}>
            {modalType === "signup" && (
              <div className="flex flex-row h-[550px]" ref={modalRef}>
                <div className="rounded-l-2xl">
                  <img
                    src={sign_up}
                    alt="signUp"
                    className="h-full w-[340px]"
                  />
                </div>
                <div className="bg-white p-6 rounded-r-2xl shadow-lg w-96 relative">
                  <button
                    className="absolute right-4 text-gray-600"
                    onClick={handleClose}
                  >
                    ✖
                  </button>

                  <div className="flex justify-center z-10">
                    <img src={logo} alt="Logo" className="h-32" />
                  </div>

                  <h2 className="text-center text-xl font-bold mt-2">
                    Sign up
                  </h2>

                  <form onSubmit={handleSubmit} className="mt-4">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-1/2 p-2 border rounded-md"
                        required
                        maxLength={50}
                      />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-1/2 p-2 border rounded-md"
                        required
                        maxLength={50}
                      />
                    </div>

                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full p-2 mt-2 border rounded-md"
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>

                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-2 mt-2 border rounded-md"
                      required
                    />

                    <input
                      type="text"
                      name="mobile"
                      placeholder="Mobile Number"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="w-full p-2 mt-2 border rounded-md"
                      required
                      maxLength={10}
                    />

                    {mobileError && (
                      <p className="text-red-500 text-sm mt-1">{mobileError}</p>
                    )}

                    <p className="text-xs text-gray-600 mt-2 text-center">
                      By continuing, you agree to our{" "}
                      <a
                        href="#"
                        className="text-pink-500"
                        onClick={handleTermsClick}
                      >
                        Terms & Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="#"
                        className="text-pink-500"
                        onClick={handlePrivacyClick}
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>

                    <button
                      type="submit"
                      className="w-full mt-4 p-2 bg-pink-500 text-white rounded-md"
                    >
                      Sign up
                    </button>
                  </form>
                </div>
              </div>
            )}

            {modalType === "terms" && (
              <TermsAndConditionsPage
                isOpen={modalType === "terms"}
                onClose={() => setModalType("signup")}
              />
            )}

            {modalType === "privacy" && (
              <PrivacyPolicyUser
                isOpen={modalType === "privacy"}
                onClose={() => setModalType("signup")}
              />
            )}
            <SignupAlert
              isOpenAlert={isOpenAlert}
              onClose={() => setIsOpenAlert(false)}
              onParentClose={handleClose}
            />
          </div>
        </div>
      )}
    </>
  );
}
